/*
	Helpers for content that comes from the website: its page components, CMS HTML and blog posts.
	Those use root-relative links (/help-center) and assets (/images/…, /labor-prayer.pdf) that
	only exist on inhishands.com, so links are routed inside the app and files load from the website.
*/

export const WEBSITE = 'https://inhishands.com'

// mobile.inhishands.com is the app itself; absolute links to it come from the CMS too.
const WEBSITE_HOSTS = ['inhishands.com', 'www.inhishands.com', 'mobile.inhishands.com']

// Files in front/public: the app's server doesn't have them (it answers with index.html).
const WEBSITE_FILE = /^\/(images|media|carousel|fonts)\/|\.(pdf|jpe?g|png|webp|gif|svg|mp4|webm)$/i

export function websiteUrl(path = '/') {
	return `${WEBSITE}${path.startsWith('/') ? path : `/${path}`}`
}

function isRootRelative(value) {
	return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')
}

// Point root-relative images and videos at the website.
export function fixAssetUrls(root) {
	if (!root?.querySelectorAll) return
	for (const el of root.querySelectorAll('img[src], source[src], video[src], video[poster], iframe[src]')) {
		for (const attr of ['src', 'poster']) {
			const value = el.getAttribute(attr)
			if (!isRootRelative(value)) continue
			if (el.tagName === 'IFRAME' || WEBSITE_FILE.test(value.split('?')[0])) el.setAttribute(attr, websiteUrl(value))
		}
	}
}

/*
	Click handler for website HTML: in-site links open the app's own page (without reloading
	the app), files open from the website, everything else (mailto:, tel:, other sites) behaves
	normally. Links marked data-website deliberately open the website, for tools the app doesn't
	have (staff pages, the kit editor).
*/
export function routeContentLink(event, router) {
	if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
	const link = event.target?.closest?.('a[href]')
	if (!link || link.hasAttribute('data-website')) return
	const href = link.getAttribute('href')
	if (!href || href.startsWith('#')) return

	let url
	try {
		url = new URL(href, window.location.href)
	} catch {
		return
	}
	if (!/^https?:$/.test(url.protocol)) return
	const sameApp = url.host === window.location.host
	if (!sameApp && !WEBSITE_HOSTS.includes(url.host)) return

	if (WEBSITE_FILE.test(url.pathname) || link.hasAttribute('download')) {
		if (sameApp) {
			event.preventDefault()
			window.open(websiteUrl(url.pathname + url.search), '_blank', 'noopener')
		}
		return
	}

	event.preventDefault()
	router.push(appPath(url))
}

// The website's old links end in a slash (/categories/water-birth/); the app's routes don't.
function appPath(url) {
	const path = url.pathname.length > 1 ? url.pathname.replace(/\/+$/, '') : url.pathname
	return path + url.search + url.hash
}

/*
	Routes every website link in the app, not only those inside CMS blocks: the announcement
	banner's link, product, category and kit descriptions, the website's own pages. Listens on
	document so it runs before the native shell's window listener (lib/native.js), which would
	otherwise open inhishands.com links in a browser tab.
*/
export function installSiteLinks(router) {
	document.addEventListener('click', (event) => routeContentLink(event, router))
}

// @error handler for website photos: drop the broken-image box rather than show it.
export function hideBrokenImage(event) {
	if (event?.target) event.target.hidden = true
}

// Watches a container that website components render into and keeps its assets pointed at the website.
export function watchAssets(root) {
	if (!root || typeof MutationObserver === 'undefined') return null
	fixAssetUrls(root)
	const observer = new MutationObserver(() => fixAssetUrls(root))
	observer.observe(root, { childList: true, subtree: true })
	return observer
}
